import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import { toast } from "sonner";

const resolveImageUrl = (url) => {
    if (!url) {
        return "";
    }
    if (url.startsWith("http")) {
        return url;
    }
    if (process.env.REACT_APP_BACKEND_URL) {
        return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
};

const resolveSetlistUrl = (token) => {
    if (!token) {
        return "";
    }
    const frontendBase = (process.env.REACT_APP_FRONTEND_URL || window.location.origin || "").replace(/\/$/, "");
    return `${frontendBase}/setlist/${token}`;
};

const formatCurrency = (value) => {
    if (value === null || value === undefined) {
        return "—";
    }
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
        return "—";
    }
    return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
    }).format(numericValue);
};

const formatDate = (value) => {
    if (!value) {
        return "";
    }
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit"
    }).format(parsed);
};

const normalizeTimeDisplay = (value) => {
    if (!value) {
        return "";
    }
    const raw = String(value).trim();
    if (!raw) {
        return "";
    }

    const isoTimeMatch = raw.match(/T(\d{2}:\d{2})(?::\d{2})?/);
    if (isoTimeMatch?.[1]) {
        return isoTimeMatch[1];
    }

    const plainTimeMatch = raw.match(/^(\d{2}:\d{2})(?::\d{2})?$/);
    if (plainTimeMatch?.[1]) {
        return plainTimeMatch[1];
    }

    return raw;
};

const formatDuration = (startTime, endTime) => {
    const start = normalizeTimeDisplay(startTime);
    const end = normalizeTimeDisplay(endTime);
    if (!start) {
        return "";
    }
    if (end) {
        return `${start} - ${end}`;
    }
    return start;
};

const formatMonthLabel = (value) => {
    return new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" }).format(value);
};

const getLocalDateKey = (value) => {
    const dateValue = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dateValue.getTime())) {
        return "";
    }
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, "0");
    const day = String(dateValue.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const parseDateOnly = (value) => {
    if (!value) {
        return null;
    }
    const parsed = new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const addDaysToDateOnly = (value, days) => {
    const parsed = parseDateOnly(value);
    if (!parsed) {
        return "";
    }
    parsed.setDate(parsed.getDate() + days);
    return getLocalDateKey(parsed);
};

const getEventComplianceDeadline = (event) => {
    if (event?.setlist_deadline) {
        return event.setlist_deadline;
    }
    return addDaysToDateOnly(event?.date, 14);
};

const stripHtml = (value) => {
    if (!value) {
        return "";
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(value, "text/html");
    const text = doc.body.textContent || "";
    return text.replace(/\u00a0/g, " ").trim();
};

const isClosedEventStatus = (status) => {
    const normalized = String(status || "").trim().toLowerCase();
    return normalized === "abgesagt" || normalized === "erledigt" || normalized === "completed";
};

const decodeHtml = (value) => {
    if (!value) {
        return "";
    }
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
};

const EventsList = ({ user, view = "all" }) => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [metricsEditing, setMetricsEditing] = useState({});
    const [metricsForm, setMetricsForm] = useState({});
    const [savingMetrics, setSavingMetrics] = useState({});
    const [adminDecision, setAdminDecision] = useState({});
    const [rejectionNotes, setRejectionNotes] = useState({});
    const [publishingEventId, setPublishingEventId] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(() => new Date());
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [dateFilter, setDateFilter] = useState(view === "mine" ? "all" : "upcoming");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [listSort, setListSort] = useState("date_asc");
    const [importingWordpress, setImportingWordpress] = useState(false);
    const isAdmin = user?.role === "admin";
    const isCalendarView = view === "all";
    const showList = !isCalendarView || isAdmin;
    const todayKey = getLocalDateKey(new Date());
    
    useEffect(() => {
        fetchEvents();
    }, [filter, view]);

    useEffect(() => {
        if (view === "mine") {
            setDateFilter("all");
        }
    }, [view]);
    
    const fetchEvents = async () => {
        try {
            setLoading(true);
            const statusFilter = isCalendarView ? "published" : (filter === "all" ? null : filter);
            const data = await apiService.getEvents(statusFilter, view === "mine");
            setEvents(data);
        } catch (error) {
            console.error("Error fetching events:", error);
            toast.error("Fehler beim Laden der Veranstaltungen");
        } finally {
            setLoading(false);
        }
    };
    
    const handleDelete = async (eventId) => {
        if (!window.confirm("Willst du diese Veranstaltung wirklich löschen?")) {
            return;
        }
        
        try {
            const result = await apiService.deleteEvent(eventId);
            toast.success(result?.soft_deleted ? result.message : "Veranstaltung gelöscht");
            fetchEvents();
        } catch (error) {
            console.error("Error deleting event:", error);
            if (error.response?.status === 409) {
                const confirmForce = window.confirm("WordPress-Löschung fehlgeschlagen. Veranstaltung trotzdem löschen?");
                if (confirmForce) {
                    try {
                        const result = await apiService.deleteEvent(eventId, true);
                        toast.success(result?.soft_deleted ? result.message : "Veranstaltung gelöscht");
                        fetchEvents();
                        return;
                    } catch (forceError) {
                        console.error("Error forcing delete:", forceError);
                    }
                }
            }
            toast.error("Fehler beim Löschen der Veranstaltung");
        }
    };

    const handleAdminDecision = async (eventId, nextStatus) => {
        try {
            let rejectionMessage = "";
            if (nextStatus === "rejected") {
                rejectionMessage = rejectionNotes[eventId] || "";
                if (!rejectionMessage.trim()) {
                    toast.error("Bitte eine Ablehnungsnachricht eingeben.");
                    return;
                }
            }
            setAdminDecision(prev => ({ ...prev, [eventId]: nextStatus }));
            const response = await apiService.approveEvent(eventId, nextStatus, rejectionMessage);
            if (response?.wordpress_publish_error) {
                toast.error(`Genehmigt, aber WordPress-Push fehlgeschlagen: ${response.wordpress_publish_error}`, {
                    duration: 10000
                });
            } else {
                toast.success(`Veranstaltung wurde ${nextStatus === "approved" ? "genehmigt" : "abgelehnt"}`);
            }
            fetchEvents();
        } catch (error) {
            console.error("Error updating approval:", error);
            toast.error("Aktion konnte nicht ausgeführt werden");
        } finally {
            setAdminDecision(prev => {
                const next = { ...prev };
                delete next[eventId];
                return next;
            });
            setRejectionNotes(prev => {
                const next = { ...prev };
                delete next[eventId];
                return next;
            });
        }
    };

    const handleManualPublish = async (eventId) => {
        try {
            setPublishingEventId(eventId);
            await apiService.publishEvent(eventId);
            toast.success("Veranstaltung veröffentlicht");
            fetchEvents();
        } catch (error) {
            console.error("Error publishing event:", error);
            const message = error.response?.data?.detail || "Veröffentlichung fehlgeschlagen";
            toast.error(message);
            fetchEvents();
        } finally {
            setPublishingEventId(null);
        }
    };

    const handleWordpressImport = async () => {
        try {
            setImportingWordpress(true);
            const result = await apiService.importWordpressEvents();
            toast.success(`Import abgeschlossen: ${result.imported} neu, ${result.linked || 0} verknüpft, ${result.skipped} übersprungen.`);
            fetchEvents();
        } catch (error) {
            console.error("Error importing WordPress events:", error);
            toast.error("WordPress-Import fehlgeschlagen");
        } finally {
            setImportingWordpress(false);
        }
    };

    const buildEditParams = (eventId, { forceReadOnly = false } = {}) => {
        const params = new URLSearchParams({ eventId: String(eventId) });
        if (view === "mine") {
            params.set("returnTo", "my-events");
        } else if (isAdmin) {
            params.set("returnTo", "admin");
        }
        if (forceReadOnly) {
            params.set("readOnly", "1");
        }
        return params;
    };

    const buildDuplicateParams = (eventId) => {
        const params = new URLSearchParams({ duplicateFrom: String(eventId) });
        if (view === "mine") {
            params.set("returnTo", "my-events");
        } else if (isAdmin) {
            params.set("returnTo", "admin");
        }
        return params;
    };

    const handleOpenEvent = (event, { forceReadOnly = false } = {}) => {
        if (!event?.id) {
            return;
        }
        const userEmail = user?.email ? user.email.toLowerCase() : "";
        const canEditEvent = isAdmin
            ? !event.imported_from_wp
            : (userEmail && event.created_by_email?.toLowerCase() === userEmail);
        const params = buildEditParams(event.id, { forceReadOnly: forceReadOnly || !canEditEvent });
        navigate(`/create?${params.toString()}`);
    };
    
    const handleCancelEvent = async (eventId) => {
        if (!window.confirm("Willst du diese Veranstaltung wirklich absagen?")) {
            return;
        }
        
        try {
            await apiService.updateEvent(eventId, { status: "abgesagt" });
            toast.success("Veranstaltung wurde abgesagt");
            fetchEvents();
        } catch (error) {
            console.error("Error cancelling event:", error);
            const message = error.response?.data?.detail || "Absagen fehlgeschlagen";
            toast.error(message);
        }
    };

    const toggleMetricsEditor = (event) => {
        setMetricsEditing(prev => {
            const next = { ...prev, [event.id]: !prev[event.id] };
            if (!prev[event.id]) {
                setMetricsForm(form => ({
                    ...form,
                    [event.id]: {
                        visitor_count: event.visitor_count !== null && event.visitor_count !== undefined ? String(event.visitor_count) : "",
                        admission_revenue: event.admission_revenue !== null && event.admission_revenue !== undefined ? String(event.admission_revenue) : "",
                        total_revenue: event.total_revenue !== null && event.total_revenue !== undefined ? String(event.total_revenue) : "",
                        dj_costs_total: event.dj_costs_total !== null && event.dj_costs_total !== undefined ? String(event.dj_costs_total) : "",
                    }
                }));
            }
            return next;
        });
    };

    const handleMetricsChange = (eventId, field, value) => {
        setMetricsForm(prev => ({
            ...prev,
            [eventId]: {
                ...prev[eventId],
                [field]: value
            }
        }));
    };

    const handleMetricsSave = async (event) => {
        const values = metricsForm[event.id] || {};
        const payload = {};

        if (values.visitor_count !== undefined && values.visitor_count !== "") {
            const parsedVisitors = parseInt(values.visitor_count, 10);
            if (Number.isNaN(parsedVisitors) || parsedVisitors < 0) {
                toast.error("Bitte gib eine gültige Besucherzahl ein.");
                return;
            }
            payload.visitor_count = parsedVisitors;
        }

        if (values.admission_revenue !== undefined && values.admission_revenue !== "") {
            const parsedAdmission = Number(values.admission_revenue);
            if (Number.isNaN(parsedAdmission)) {
                toast.error("Bitte gib einen gültigen Betrag für die Eintrittseinnahmen ein.");
                return;
            }
            payload.admission_revenue = parsedAdmission;
        }

        if (values.total_revenue !== undefined && values.total_revenue !== "") {
            const parsedTotal = Number(values.total_revenue);
            if (Number.isNaN(parsedTotal)) {
                toast.error("Bitte gib einen gültigen Gesamtumsatz ein.");
                return;
            }
            payload.total_revenue = parsedTotal;
        }

        if (values.dj_costs_total !== undefined && values.dj_costs_total !== "") {
            const parsedDjCosts = Number(values.dj_costs_total);
            if (Number.isNaN(parsedDjCosts)) {
                toast.error("Bitte gib gültige DJ-Gesamtkosten ein.");
                return;
            }
            payload.dj_costs_total = parsedDjCosts;
        }

        if (Object.keys(payload).length === 0) {
            toast.error("Bitte trag mindestens einen Wert ein.");
            return;
        }

        try {
            setSavingMetrics(prev => ({ ...prev, [event.id]: true }));
            await apiService.updateEvent(event.id, payload);
            toast.success("Kennzahlen gespeichert.");
            setMetricsEditing(prev => ({ ...prev, [event.id]: false }));
            fetchEvents();
        } catch (error) {
            console.error("Error updating metrics:", error);
            const message = error.response?.data?.detail || "Kennzahlen konnten nicht gespeichert werden.";
            toast.error(message);
        } finally {
            setSavingMetrics(prev => ({ ...prev, [event.id]: false }));
        }
    };
    
    const getStatusBadge = (status) => {
        const badges = {
            pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
            approved: "bg-green-100 text-green-800 border-green-200",
            rejected: "bg-red-100 text-red-800 border-red-200",
            published: "bg-blue-100 text-blue-800 border-blue-200",
            abgesagt: "bg-gray-100 text-gray-800 border-gray-200",
        };
        
        const labels = {
            pending: "Ausstehend",
            approved: "Genehmigt",
            rejected: "Abgelehnt",
            published: "Veröffentlicht",
            abgesagt: "Abgesagt",
        };
        
        if (!badges[status]) {
            return null;
        }
        
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${badges[status]}`}>
                {labels[status]}
            </span>
        );
    };
    
    const showAdminSummary = isAdmin && view !== "mine" && !isCalendarView;
    const summary = useMemo(() => {
        const counts = {
            total: events.length,
            pending: 0,
            approved: 0,
            rejected: 0,
            published: 0,
            mine: 0,
        };
        const userEmail = (user?.email || "").toLowerCase();
        events.forEach((event) => {
            counts[event.status] = (counts[event.status] || 0) + 1;
            if (event.created_by_email && event.created_by_email.toLowerCase() === userEmail) {
                counts.mine += 1;
            }
        });
        return counts;
    }, [events, user]);

    const calendarEvents = useMemo(() => {
        const published = events.filter((event) => event.status === "published");
        if (selectedCategory === "all") {
            return published;
        }
        return published.filter((event) => decodeHtml(event.category) === selectedCategory);
    }, [events, selectedCategory]);

    const calendarCategories = useMemo(() => {
        const unique = new Set();
        events.forEach((event) => {
            if (event.status === "published") {
                unique.add(decodeHtml(event.category));
            }
        });
        return Array.from(unique).sort((a, b) => a.localeCompare(b));
    }, [events]);

    const calendarDays = useMemo(() => {
        const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
        const leadingEmpty = (start.getDay() + 6) % 7;
        const daysInMonth = end.getDate();
        const eventsByDate = calendarEvents.reduce((acc, event) => {
            if (!event.date) {
                return acc;
            }
            const key = event.date;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(event);
            return acc;
        }, {});

        const cells = [];
        for (let i = 0; i < leadingEmpty; i += 1) {
            cells.push({ date: null, events: [] });
        }
        for (let day = 1; day <= daysInMonth; day += 1) {
            const dateValue = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
            const dateKey = getLocalDateKey(dateValue);
            const dayEvents = (eventsByDate[dateKey] || []).sort((a, b) => (a.time || "").localeCompare(b.time || ""));
            cells.push({ date: dateValue, events: dayEvents });
        }
        return cells;
    }, [calendarEvents, currentMonth]);

    const icsUrl = useMemo(() => {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        const params = new URLSearchParams();
        if (selectedCategory !== "all") {
            params.set("category", selectedCategory);
        }
        const query = params.toString();
        return `${base}/api/public/events/ics${query ? `?${query}` : ""}`;
    }, [selectedCategory]);

    const listEvents = useMemo(() => {
        let items = [...events];
        if (selectedCategory !== "all") {
            items = items.filter((event) => decodeHtml(event.category) === selectedCategory);
        }
        if (dateFilter === "upcoming") {
            items = items.filter((event) => event.date && event.date >= todayKey);
        } else if (dateFilter === "past") {
            items = items.filter((event) => event.date && event.date < todayKey);
        } else if (dateFilter === "range") {
            if (dateFrom) {
                items = items.filter((event) => event.date && event.date >= dateFrom);
            }
            if (dateTo) {
                items = items.filter((event) => event.date && event.date <= dateTo);
            }
        }
        const byDateTime = (a, b) => {
            const dateCompare = (a.date || "").localeCompare(b.date || "");
            if (dateCompare !== 0) {
                return dateCompare;
            }
            return (a.time || "").localeCompare(b.time || "");
        };
        const byCategory = (a, b) => decodeHtml(a.category).localeCompare(decodeHtml(b.category));
        const byTitle = (a, b) => (a.title || "").localeCompare(b.title || "");

        items.sort((a, b) => {
            switch (listSort) {
                case "date_desc":
                    return byDateTime(b, a);
                case "title_asc":
                    return byTitle(a, b);
                case "title_desc":
                    return byTitle(b, a);
                case "category_asc":
                    return byCategory(a, b);
                case "category_desc":
                    return byCategory(b, a);
                default:
                    return byDateTime(a, b);
            }
        });
        return items;
    }, [events, selectedCategory, dateFilter, dateFrom, dateTo, listSort]);

    const mineKpis = useMemo(() => {
        if (view !== "mine") {
            return null;
        }
        const today = parseDateOnly(todayKey);
        const kpis = {
            total: 0,
            overdue_setlist: 0,
            overdue_metrics: 0,
            total_revenue: 0,
        };
        listEvents.forEach((event) => {
            kpis.total += 1;
            const deadlineDate = parseDateOnly(getEventComplianceDeadline(event));
            const isOverdue = !!(today && deadlineDate && deadlineDate < today && !isClosedEventStatus(event.status));
            const setlistMissing = event.gema_relevant
                && event.is_setlist_required
                && (event.setlist_status || "Ausstehend") !== "Eingereicht";
            const metricsMissing = event.visitor_count === null || event.visitor_count === undefined
                || event.admission_revenue === null || event.admission_revenue === undefined
                || event.total_revenue === null || event.total_revenue === undefined
                || event.dj_costs_total === null || event.dj_costs_total === undefined;
            if (isOverdue && setlistMissing) {
                kpis.overdue_setlist += 1;
            }
            if (isOverdue && metricsMissing) {
                kpis.overdue_metrics += 1;
            }
            const totalRevenue = Number(event.total_revenue);
            if (!Number.isNaN(totalRevenue)) {
                kpis.total_revenue += totalRevenue;
            }
        });
        return kpis;
    }, [listEvents, todayKey, view]);

    const handleDateFromChange = (eventValue) => {
        const nextValue = eventValue.target.value;
        if (nextValue && nextValue < todayKey) {
            toast.error("Bitte ein zukünftiges Datum wählen.");
            return;
        }
        if (nextValue && dateTo && nextValue > dateTo) {
            toast.error("Startdatum darf nicht nach dem Enddatum liegen.");
            return;
        }
        setDateFrom(nextValue);
    };

    const handleDateToChange = (eventValue) => {
        const nextValue = eventValue.target.value;
        if (nextValue && nextValue < todayKey) {
            toast.error("Bitte ein zukünftiges Datum wählen.");
            return;
        }
        if (nextValue && dateFrom && nextValue < dateFrom) {
            toast.error("Enddatum darf nicht vor dem Startdatum liegen.");
            return;
        }
        setDateTo(nextValue);
    };
    
    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                        {view === "mine" ? "Meine Veranstaltungen" : "Veranstaltungskalender"}
                    </h2>
                    <p className="text-sm text-gray-500">
                        {view === "mine"
                            ? "Übersicht deiner gemeldeten Veranstaltungen inkl. Bearbeitung."
                            : "Alle veröffentlichten Veranstaltungen im Überblick."}
                    </p>
                    {view === "mine" && (
                        <div className="mt-3 flex flex-wrap items-center gap-3">
                            <p className="text-xs text-gray-600">
                                Kontakt für Reminder: <span className="font-semibold text-gray-800">{user?.notifications_email || user?.email || "nicht gesetzt"}</span>
                            </p>
                            <Link
                                to="/my-events?openContactSetup=1"
                                className="inline-flex items-center rounded-md border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-100"
                            >
                                Alternative E-Mail ändern
                            </Link>
                        </div>
                    )}
                </div>
                {!isCalendarView && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex flex-wrap gap-2">
                            <button
                                data-testid="filter-all"
                                onClick={() => setFilter("all")}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                    filter === "all" 
                                        ? "bg-purple-600 text-white" 
                                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                }`}
                            >
                                Alle
                            </button>
                            <button
                                data-testid="filter-pending"
                                onClick={() => setFilter("pending")}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                    filter === "pending" 
                                        ? "bg-purple-600 text-white" 
                                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                }`}
                            >
                                Ausstehend
                            </button>
                            <button
                                data-testid="filter-approved"
                                onClick={() => setFilter("approved")}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                    filter === "approved" 
                                        ? "bg-purple-600 text-white" 
                                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                }`}
                            >
                                Genehmigt
                            </button>
                            <button
                                data-testid="filter-published"
                                onClick={() => setFilter("published")}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                    filter === "published" 
                                        ? "bg-purple-600 text-white" 
                                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                }`}
                            >
                                Veröffentlicht
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700" htmlFor="my-date-filter">
                                Zeitraum
                            </label>
                            <select
                                id="my-date-filter"
                                value={dateFilter}
                                onChange={(eventValue) => setDateFilter(eventValue.target.value)}
                                className="w-full sm:w-40 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="upcoming">Kommende</option>
                                <option value="past">Vergangene</option>
                                <option value="all">Alle</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {isCalendarView && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                            >
                                <span className="sr-only">Vorheriger Monat</span>
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <p className="text-lg font-semibold text-gray-900">{formatMonthLabel(currentMonth)}</p>
                            <button
                                type="button"
                                onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                            >
                                <span className="sr-only">Nächster Monat</span>
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                            {isAdmin && (
                                <button
                                    type="button"
                                    onClick={handleWordpressImport}
                                    disabled={importingWordpress}
                                    className="ml-2 inline-flex items-center gap-2 rounded-lg border border-purple-200 px-4 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-50 disabled:opacity-60"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    {importingWordpress ? "Import läuft..." : "WordPress importieren"}
                                </button>
                            )}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <label className="text-sm font-medium text-gray-700" htmlFor="category-filter">
                                Kategorie
                            </label>
                            <select
                                id="category-filter"
                                value={selectedCategory}
                                onChange={(eventValue) => setSelectedCategory(eventValue.target.value)}
                                className="w-full sm:w-64 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="all">Alle Kategorien</option>
                                {calendarCategories.map((category) => (
                                    <option key={category} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </select>
                            <a
                                href={icsUrl}
                                className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                            >
                                ICS Export
                            </a>
                        </div>
                    </div>

                    <div className="grid grid-cols-7 gap-2 text-xs font-semibold text-gray-500 sm:grid-cols-7">
                        {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((label) => (
                            <div key={label} className="text-center">
                                {label}
                            </div>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-7 sm:gap-2">
                        {calendarDays.map((day, index) => (
                            <div
                                key={`${day.date ? day.date.toISOString() : "empty"}-${index}`}
                                className={`rounded-lg border border-gray-200 p-3 text-sm sm:min-h-[120px] sm:p-2 ${
                                    day.date ? "bg-white" : "bg-gray-50"
                                } ${day.date ? "" : "hidden sm:block"}`}
                            >
                                {day.date && (
                                    <>
                                        <p className="text-xs font-semibold text-gray-700 sm:text-xs">
                                            {day.date.getDate()}. {day.date.toLocaleDateString("de-DE", { weekday: "short" })}
                                        </p>
                                        <div className="mt-2 space-y-2 sm:mt-2 sm:space-y-2">
                                            {day.events.length === 0 && (
                                                <p className="text-xs text-gray-400">Keine Events</p>
                                            )}
                                            {day.events.map((event) => (
                                                <div
                                                    key={event.id}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => handleOpenEvent(event, { forceReadOnly: true })}
                                                    onKeyDown={(eventKey) => {
                                                        if (eventKey.key === "Enter" || eventKey.key === " ") {
                                                            eventKey.preventDefault();
                                                            handleOpenEvent(event, { forceReadOnly: true });
                                                        }
                                                    }}
                                                    className="rounded-md bg-purple-50 px-3 py-2 text-xs text-purple-900 cursor-pointer hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-purple-500 sm:px-2 sm:py-1"
                                                >
                                                    <p className="font-semibold break-words leading-snug">{event.title}</p>
                                                    <p className="text-purple-800">{formatDuration(event.time, event.end_time)}</p>
                                                    <p className="text-purple-700 break-words">{decodeHtml(event.category)}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {isCalendarView && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Übersicht veröffentlichter Events</h3>
                            <p className="text-sm text-gray-500">Standardmäßig werden nur kommende Veranstaltungen angezeigt.</p>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <label className="text-sm font-medium text-gray-700" htmlFor="date-filter">
                                Zeitraum
                            </label>
                            <select
                                id="date-filter"
                                value={dateFilter}
                                onChange={(eventValue) => setDateFilter(eventValue.target.value)}
                                className="w-full sm:w-48 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="upcoming">Kommende</option>
                                <option value="all">Alle</option>
                                <option value="past">Vergangene</option>
                                <option value="range">Zeitraum wählen</option>
                            </select>
                            {dateFilter === "range" && (
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                    <input
                                        type="date"
                                        value={dateFrom}
                                        min={todayKey}
                                        onChange={handleDateFromChange}
                                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                                    />
                                    <input
                                        type="date"
                                        value={dateTo}
                                        min={todayKey}
                                        onChange={handleDateToChange}
                                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                            )}
                            <label className="text-sm font-medium text-gray-700" htmlFor="sort-filter">
                                Sortierung
                            </label>
                            <select
                                id="sort-filter"
                                value={listSort}
                                onChange={(eventValue) => setListSort(eventValue.target.value)}
                                className="w-full sm:w-56 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                            >
                                <option value="date_asc">Datum (aufsteigend)</option>
                                <option value="date_desc">Datum (absteigend)</option>
                                <option value="title_asc">Titel (A-Z)</option>
                                <option value="title_desc">Titel (Z-A)</option>
                                <option value="category_asc">Kategorie (A-Z)</option>
                                <option value="category_desc">Kategorie (Z-A)</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}
            
            {showAdminSummary && (
                <div className="grid gap-4 md:grid-cols-5">
                    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                        <p className="text-sm text-gray-500">Genehmigt</p>
                        <p className="text-3xl font-bold text-gray-900">{summary.approved}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                        <p className="text-sm text-gray-500">Ausstehend</p>
                        <p className="text-3xl font-bold text-gray-900">{summary.pending}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                        <p className="text-sm text-gray-500">Veröffentlicht</p>
                        <p className="text-3xl font-bold text-gray-900">{summary.published}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                        <p className="text-sm text-gray-500">Gemeldet gesamt</p>
                        <p className="text-3xl font-bold text-gray-900">{summary.total}</p>
                    </div>
                    <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 flex flex-col gap-2">
                        <div>
                            <p className="text-sm font-semibold text-purple-700">Eigene Meldungen</p>
                            <p className="text-2xl font-bold text-purple-900">{summary.mine}</p>
                        </div>
                        <Link
                            to="/my-events"
                            className="inline-flex items-center justify-center rounded-lg bg-purple-600 text-white text-sm font-semibold py-2 hover:bg-purple-700 transition-colors"
                        >
                            Meine Meldungen öffnen
                        </Link>
                    </div>
                </div>
            )}

            {view === "mine" && mineKpis && (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                        <p className="text-sm text-gray-500">Meine Meldungen</p>
                        <p className="text-3xl font-bold text-gray-900">{mineKpis.total}</p>
                    </div>
                    <div className={`rounded-xl border p-4 shadow-sm ${mineKpis.overdue_setlist > 0 ? "bg-red-50 border-red-200" : "bg-white border-gray-200"}`}>
                        <p className={`text-sm ${mineKpis.overdue_setlist > 0 ? "text-red-700 font-semibold" : "text-gray-500"}`}>
                            {mineKpis.overdue_setlist > 0 ? "! Setlist fällig" : "Setlist fällig"}
                        </p>
                        <p className={`text-3xl font-bold ${mineKpis.overdue_setlist > 0 ? "text-red-700" : "text-gray-900"}`}>{mineKpis.overdue_setlist}</p>
                    </div>
                    <div className={`rounded-xl border p-4 shadow-sm ${mineKpis.overdue_metrics > 0 ? "bg-red-50 border-red-200" : "bg-white border-gray-200"}`}>
                        <p className={`text-sm ${mineKpis.overdue_metrics > 0 ? "text-red-700 font-semibold" : "text-gray-500"}`}>
                            {mineKpis.overdue_metrics > 0 ? "! Kennzahlen fällig" : "Kennzahlen fällig"}
                        </p>
                        <p className={`text-3xl font-bold ${mineKpis.overdue_metrics > 0 ? "text-red-700" : "text-gray-900"}`}>{mineKpis.overdue_metrics}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                        <p className="text-sm text-gray-500">Gesamteinnahmen</p>
                        <p className="text-3xl font-bold text-gray-900">{formatCurrency(mineKpis.total_revenue)}</p>
                    </div>
                </div>
            )}
            
            {showList && (listEvents.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center" data-testid="no-events">
                    <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-gray-600 text-lg">Keine Veranstaltungen gefunden</p>
                    <p className="text-gray-500 text-sm mt-2">Erstelle deine erste Veranstaltung</p>
                </div>
            ) : (
                <div className="grid gap-6">
                    {listEvents.map((event) => {
                        const coverImage = resolveImageUrl(event.cover_image_url || event.image_url);
                        const galleryImages = Array.isArray(event.gallery_image_urls) ? event.gallery_image_urls : [];
                        const currentMetrics = metricsForm[event.id] || {
                            visitor_count: event.visitor_count !== null && event.visitor_count !== undefined ? String(event.visitor_count) : "",
                            admission_revenue: event.admission_revenue !== null && event.admission_revenue !== undefined ? String(event.admission_revenue) : "",
                            total_revenue: event.total_revenue !== null && event.total_revenue !== undefined ? String(event.total_revenue) : "",
                            dj_costs_total: event.dj_costs_total !== null && event.dj_costs_total !== undefined ? String(event.dj_costs_total) : "",
                        };
                        const hasMetrics = event.visitor_count !== null && event.visitor_count !== undefined
                            || event.admission_revenue !== null && event.admission_revenue !== undefined
                            || event.total_revenue !== null && event.total_revenue !== undefined
                            || event.dj_costs_total !== null && event.dj_costs_total !== undefined;
                        const userEmail = user?.email ? user.email.toLowerCase() : "";
                        const canEdit = isAdmin
                            ? !event.imported_from_wp
                            : view === "mine" && (userEmail && event.created_by_email?.toLowerCase() === userEmail);
                        const canEditMetrics = (isAdmin || canEdit) && (event.status === "approved" || event.status === "published");
                        const isEditingMetrics = !!metricsEditing[event.id] && canEditMetrics;
                        const today = parseDateOnly(todayKey);
                        const complianceDeadline = getEventComplianceDeadline(event);
                        const deadlineDate = parseDateOnly(complianceDeadline);
                        const deadlineOverdue = !!(today && deadlineDate && deadlineDate < today && !isClosedEventStatus(event.status));
                        const setlistMissing = event.gema_relevant
                            && event.is_setlist_required
                            && (event.setlist_status || "Ausstehend") !== "Eingereicht";
                        const setlistOverdue = setlistMissing && deadlineOverdue;
                        const metricsMissing = event.visitor_count === null || event.visitor_count === undefined
                            || event.admission_revenue === null || event.admission_revenue === undefined
                            || event.total_revenue === null || event.total_revenue === undefined
                            || event.dj_costs_total === null || event.dj_costs_total === undefined;
                        const metricsOverdue = metricsMissing && deadlineOverdue;

                        return (
                            <div 
                                key={event.id} 
                                data-testid={`event-${event.id}`}
                                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden border border-gray-200"
                            >
                                <div className="md:flex">
                                    {coverImage && (
                                        <div className="md:w-64 h-48 md:h-auto flex-shrink-0">
                                            <img 
                                                src={coverImage} 
                                                alt={event.title}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    )}
                                    <div className="p-6 flex-1">
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h3 className="text-xl font-bold text-gray-900" data-testid="event-title">
                                                        {event.title}
                                                    </h3>
                                                    {getStatusBadge(event.status)}
                                                </div>
                                                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                                    <div className="flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                        <span>{formatDate(event.date)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <span>{formatDuration(event.time, event.end_time) || "Zeit offen"}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                        </svg>
                                                        <span className="font-medium">{decodeHtml(event.category)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                {user?.role === "admin" && event.status === "pending" && (
                                                    <div className="flex flex-col gap-3">
                                                        <textarea
                                                            value={rejectionNotes[event.id] || ""}
                                                            onChange={(eventValue) =>
                                                                setRejectionNotes(prev => ({
                                                                    ...prev,
                                                                    [event.id]: eventValue.target.value
                                                                }))
                                                            }
                                                            rows={3}
                                                            placeholder="Ablehnungsnachricht für den Veranstalter"
                                                            className="w-72 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                                                        />
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => handleAdminDecision(event.id, "approved")}
                                                                disabled={!!adminDecision[event.id]}
                                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
                                                            >
                                                                {adminDecision[event.id] === "approved" ? "Genehmigt..." : "Genehmigen"}
                                                            </button>
                                                            <button
                                                                onClick={() => handleAdminDecision(event.id, "rejected")}
                                                                disabled={!!adminDecision[event.id]}
                                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
                                                            >
                                                                {adminDecision[event.id] === "rejected" ? "Ablehnen..." : "Ablehnen"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                {canEdit && (
                                                    <>
                                                        <Link
                                                            to={`/create?${buildDuplicateParams(event.id).toString()}`}
                                                            aria-label="Veranstaltung duplizieren"
                                                            title="Duplizieren"
                                                            className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-indigo-300 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-800 transition-colors"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2M10 20h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                            </svg>
                                                        </Link>
                                                        <Link
                                                            to={`/create?${buildEditParams(event.id).toString()}`}
                                                            aria-label="Veranstaltung bearbeiten"
                                                            title="Bearbeiten"
                                                            className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 11l6.586-6.586a2 2 0 112.828 2.828L11.828 13.828a2 2 0 01-1.414.586H9v-3.414a2 2 0 01.586-1.414z" />
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 19H5a2 2 0 01-2-2V8" />
                                                            </svg>
                                                        </Link>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {event.status === "rejected" && event.rejection_message && (
                                            <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                                                <p className="text-sm font-semibold text-gray-900">Ablehnungsnachricht</p>
                                                <p className="text-sm text-gray-700 mt-2">{event.rejection_message}</p>
                                                {canEdit && (
                                                    <Link
                                                        to={`/create?${buildEditParams(event.id).toString()}`}
                                                        className="mt-3 inline-flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900"
                                                    >
                                                        Bearbeiten und erneut einreichen
                                                    </Link>
                                                )}
                                            </div>
                                        )}
                                        {(event.admin_note || event.additional_info) && (
                                            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                                                <p className="text-sm font-semibold text-blue-800">Info an Admin</p>
                                                <p className="text-sm text-blue-700 mt-2">{event.admin_note || event.additional_info}</p>
                                            </div>
                                        )}

                                        {(setlistOverdue || metricsOverdue) && (
                                            <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3">
                                                <p className="text-sm font-semibold text-red-800">
                                                    ! Frist überschritten
                                                </p>
                                                <p className="text-sm text-red-700 mt-1">
                                                    {setlistOverdue && "Setlist fehlt. "}
                                                    {metricsOverdue && "Kennzahlen sind unvollständig. "}
                                                    {complianceDeadline ? `Deadline war ${formatDate(complianceDeadline)}.` : ""}
                                                </p>
                                            </div>
                                        )}

                                        <p className="text-gray-700 mb-4 line-clamp-2">{stripHtml(event.description)}</p>

                                        <div className="space-y-3 mb-4">
                                            {event.is_kultur_for_all && (
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                                                    Kultur für alle
                                                </span>
                                            )}
                                            {event.sold_out && (
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                                                    Ausverkauft
                                                </span>
                                            )}

                                            {(event.homepage_url || event.ticket_url) && (
                                                <div className="flex flex-wrap gap-4 text-sm text-purple-700">
                                                    {event.homepage_url && (
                                                        <a
                                                            href={event.homepage_url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="hover:underline"
                                                        >
                                                            Homepage
                                                        </a>
                                                    )}
                                                    {event.ticket_url && (
                                                        <a
                                                            href={event.ticket_url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="hover:underline"
                                                        >
                                                            Tickets
                                                        </a>
                                                    )}
                                                </div>
                                            )}

                                            {event.gema_relevant && event.is_setlist_required && event.setlist_public_token && (
                                                <div className={`rounded-lg border px-4 py-3 ${setlistOverdue ? "border-red-300 bg-red-50" : "border-indigo-200 bg-indigo-50"}`}>
                                                    <p className={`text-sm font-semibold mb-2 ${setlistOverdue ? "text-red-800" : "text-indigo-800"}`}>
                                                        {setlistOverdue ? "! Setlist überfällig" : "Setlist erforderlich"}
                                                    </p>
                                                    <p className={`text-xs mb-3 ${setlistOverdue ? "text-red-700" : "text-indigo-700"}`}>
                                                        {complianceDeadline ? ` Deadline: ${formatDate(complianceDeadline)}.` : ""}
                                                    </p>
                                                    <a
                                                        href={resolveSetlistUrl(event.setlist_public_token)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold text-white ${setlistOverdue ? "bg-red-600 hover:bg-red-700" : "bg-indigo-600 hover:bg-indigo-700"}`}
                                                    >
                                                        Setlist jetzt hinterlegen
                                                    </a>
                                                </div>
                                            )}

                                            {Array.isArray(event.pricing_tiers) && event.pricing_tiers.length > 0 && (
                                                <div className="text-sm text-gray-700">
                                                    <p className="font-semibold text-gray-900">Staffelpreise:</p>
                                                    <ul className="mt-1 space-y-1">
                                                        {event.pricing_tiers.map((tier, index) => (
                                                            <li key={index}>
                                                                <span className="font-medium">{tier.label || "Preis"}:</span>{" "}
                                                                <span>{tier.price}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>

                                        {galleryImages.length > 0 && (
                                            <div className="mb-4">
                                                <p className="text-sm font-semibold text-gray-900 mb-2">Weitere Bilder</p>
                                                <div className="flex flex-wrap gap-3">
                                                    {galleryImages.map((url, index) => (
                                                        <img
                                                            key={`${url}-${index}`}
                                                            src={resolveImageUrl(url)}
                                                            alt={`Galeriebild ${index + 1}`}
                                                            className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {isAdmin && (event.wordpress_publish_error || (event.status === "approved" && !event.wordpress_url)) && (
                                            <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-red-900">
                                                <p className="text-sm font-semibold">Nicht auf WordPress veröffentlicht</p>
                                                <p className="mt-1 break-words text-xs">
                                                    {event.wordpress_publish_error || "Der automatische WordPress-Push war nicht erfolgreich. Für diesen älteren Fehler sind keine Details gespeichert."}
                                                </p>
                                                {event.wordpress_publish_failed_at && (
                                                    <p className="mt-1 text-xs text-red-700">
                                                        Letzter Versuch: {new Date(event.wordpress_publish_failed_at).toLocaleString("de-DE")}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="text-sm">
                                                    <span className="text-gray-600">Eintritt: </span>
                                                    <span className="font-semibold text-gray-900">{event.admission}</span>
                                                </div>
                                                {event.wordpress_url && (
                                                    <a
                                                        href={event.wordpress_url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-xs text-purple-600 underline"
                                                    >
                                                        Auf der Webseite anzeigen
                                                    </a>
                                                )}
                                            </div>
                                            
                                            <div className="flex items-center gap-3">
                                                {(event.status === "pending" || isAdmin) && (
                                                    <button 
                                                        data-testid={`delete-${event.id}`}
                                                        onClick={() => handleDelete(event.id)}
                                                        className="text-red-600 hover:text-red-700 font-medium text-sm flex items-center gap-1 transition-colors"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                        Löschen
                                                    </button>
                                                )}
                                                {canEdit && (event.status === "approved" || event.status === "published") && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCancelEvent(event.id)}
                                                        className="text-sm text-gray-600 font-semibold hover:text-gray-800"
                                                    >
                                                        Absagen
                                                    </button>
                                                )}
                                                {user?.role === "admin" && event.status === "approved" && !event.wordpress_url && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleManualPublish(event.id)}
                                                        className="text-sm text-purple-600 font-semibold disabled:opacity-50"
                                                        disabled={publishingEventId === event.id}
                                                    >
                                                        {publishingEventId === event.id
                                                            ? "Veröffentliche..."
                                                            : (event.wordpress_publish_error ? "Erneut zu WordPress pushen" : "Manuell veröffentlichen")}
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="border-t border-gray-200 pt-4 mt-6 space-y-4">
                                            <div className="flex items-center justify-between">
                                                <p className={`text-sm font-semibold ${metricsOverdue ? "text-red-700" : "text-gray-900"}`}>
                                                    {metricsOverdue ? "! Kennzahlen (fällig)" : "Kennzahlen"}
                                                </p>
                                                {canEditMetrics && (
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleMetricsEditor(event)}
                                                        className="text-sm font-medium text-purple-600 hover:text-purple-700"
                                                    >
                                                        {isEditingMetrics ? "Abbrechen" : "Kennzahlen bearbeiten"}
                                                    </button>
                                                )}
                                            </div>
                                            {hasMetrics ? (
                                                <div className="grid gap-3 sm:grid-cols-4 text-sm text-gray-700">
                                                    <div>
                                                        <p className="font-semibold text-gray-900">Besucher</p>
                                                        <p>{event.visitor_count ?? "—"}</p>
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-gray-900">Eintrittseinnahmen</p>
                                                        <p>{formatCurrency(event.admission_revenue)}</p>
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-gray-900">Gesamtumsatz</p>
                                                        <p>{formatCurrency(event.total_revenue)}</p>
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-gray-900">Gesamtgage</p>
                                                        <p>{formatCurrency(event.dj_costs_total)}</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-sm text-gray-500">Noch keine Kennzahlen hinterlegt.</p>
                                            )}

                                            {isEditingMetrics && (
                                                <div className="space-y-4">
                                                    <div className="grid gap-4 md:grid-cols-4">
                                                        <div>
                                                            <label className="block text-xs font-semibold text-gray-600 mb-2">
                                                                Besucherzahl
                                                            </label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                value={currentMetrics.visitor_count}
                                                                onChange={(eventInput) => handleMetricsChange(event.id, "visitor_count", eventInput.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-semibold text-gray-600 mb-2">
                                                                Eintrittseinnahmen (€)
                                                            </label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={currentMetrics.admission_revenue}
                                                                onChange={(eventInput) => handleMetricsChange(event.id, "admission_revenue", eventInput.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-semibold text-gray-600 mb-2">
                                                                Gesamtumsatz (€)
                                                            </label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={currentMetrics.total_revenue}
                                                                onChange={(eventInput) => handleMetricsChange(event.id, "total_revenue", eventInput.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-semibold text-gray-600 mb-2">
                                                                Gesamtgage (€)
                                                            </label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={currentMetrics.dj_costs_total}
                                                                onChange={(eventInput) => handleMetricsChange(event.id, "dj_costs_total", eventInput.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-end">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleMetricsSave(event)}
                                                            disabled={savingMetrics[event.id]}
                                                            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                                                        >
                                                            {savingMetrics[event.id] ? "Speichern..." : "Kennzahlen speichern"}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

export default EventsList;
